import { Table, Text, Group, Paper, Title, Notification, Loader } from '@mantine/core';
import {  ICampaignTransHistory, ICurrencyRates } from '../types';
import React, { useEffect, useState } from 'react';
import { makeGetRequest } from '../services/httpHelper';
import { convertCurrency, isErrorObject } from '../utils/utils';
import ErrorHandler from '../services/ErrorHandler';
import { IconAlertCircle } from '@tabler/icons-react';

type transProps = {
    transactions: ICampaignTransHistory[];
    campaignId: string;
    campaign_currency: string;
};

const TransactionHistory: React.FC<transProps> = ({ transactions,  campaign_currency }) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [exchangeRates, setExchangeRates] = useState<ICurrencyRates>();

    const showHistorySummary = false;

    // Fetch exchange rates
    const fetchExchangeRates = async () => {
        const endpoint = "latest-currency-exchange-rates";
        setIsLoading(true);
        const res = await makeGetRequest(endpoint);
        if (isErrorObject(res)) {
            setError(ErrorHandler(res));
        } else {
            setExchangeRates(res.data);
            setIsLoading(false);
        }
    };

    // Currency conversion handler
    const handleCurrencyConversion = (amount: number, fromCurrency: string, toCurrency: string) => {
        return convertCurrency(amount, fromCurrency, toCurrency, exchangeRates?.rates || {});
    };

    // Calculate total deposits, withdrawals, service fees, and balance
    const totalDeposits = transactions.reduce((acc, transaction) => {
        if (transaction.type === 'in') {
            const convertedAmount = handleCurrencyConversion(transaction.amount, transaction.currency, campaign_currency);
            return acc + (convertedAmount?.amount || 0);
        }
        return acc;
    }, 0);

    const totalWithdrawals = transactions.reduce((acc, transaction) => {
        if (transaction.type === 'withdrawal') {
            const convertedAmount = handleCurrencyConversion(transaction.amount, transaction.currency, campaign_currency);
            return acc + (convertedAmount?.amount || 0);
        }
        return acc;
    }, 0);

    /*const totalServiceFees = transactions.reduce((acc, transaction) => {
        const fee = (transaction.processor_fee || 0) + (transaction.service_fee || 0);
        const convertedFee = handleCurrencyConversion(fee, transaction.currency, campaign_currency);
        return acc + (convertedFee?.amount || 0);
    }, 0);*/
    const totalServiceFees =0;

    const currentBalance = totalDeposits - totalWithdrawals - totalServiceFees;

    useEffect(() => {
        fetchExchangeRates();
    }, []);

    // Table rows
    const rows = transactions.map((transaction: ICampaignTransHistory, index: number) => (
        <tr key={index}>
            <td>{new Date(transaction.date).toLocaleString()}</td>
            <td>{transaction.description}</td>
            <td>{transaction.currency}</td>
            <td>
                <Text color={transaction.type === 'in' ? 'green' : 'red'} weight={500}>
                    {transaction.type === 'in' ? '+' : '-'}
                    {transaction.amount.toLocaleString(undefined, {
                        style: 'currency',
                        currency: transaction.currency || 'KES',
                    })}
                </Text>
            </td>
        </tr>
    ));

    return (
        <Paper withBorder radius="md" p="md">
            <Group position="apart" mb="md">
                <Title order={3}>Transaction Summary</Title>
                {isLoading && <Loader />}
                {error && (
                    <Notification icon={<IconAlertCircle size={18} />} color="red" mb="lg">
                        {error}
                    </Notification>
                )}
                {
                  showHistorySummary && 
                  <Group spacing="xl">
                    <div>
                        <Text>Total Deposits:</Text>
                        <Text weight={700} color="green">
                            {totalDeposits.toLocaleString(undefined, {
                                style: 'currency',
                                currency: campaign_currency,
                            })}
                        </Text>
                    </div>
                    <div>
                        <Text>Total Service Fees:</Text>
                        <Text weight={700} color="orange">
                            {totalServiceFees.toLocaleString(undefined, {
                                style: 'currency',
                                currency: campaign_currency,
                            })}
                        </Text>
                    </div>
                    <div>
                        <Text>Total Withdrawals:</Text>
                        <Text weight={700} color="red">
                            {totalWithdrawals.toLocaleString(undefined, {
                                style: 'currency',
                                currency: campaign_currency,
                            })}
                        </Text>
                    </div>
                    <div>
                        <Text>Current Balance:</Text>
                        <Text weight={700} color={currentBalance > 0 ? 'green' : 'gray'}>
                            {currentBalance.toLocaleString(undefined, {
                                style: 'currency',
                                currency: campaign_currency,
                            })}
                        </Text>
                    </div>
                </Group>
                }
            </Group>

            <Table striped highlightOnHover>
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Currency</th>
                        <th>Amount</th>
                    </tr>
                </thead>
                <tbody>{rows}</tbody>
            </Table>
        </Paper>
    );
};

export default TransactionHistory;
